import { useQuery } from '@tanstack/react-query';
import { CONTAINER_KIND_LABELS } from '@cerebro-diem/core';
import type { ContainerKind, ContainerRow, ProjectStatus } from '@cerebro-diem/core';
import { signedInUserId, supabase } from './supabase';
import { LIST_LIMIT, queryKeys } from './queries';

/** The table each kind of container lives in. */
export const CONTAINER_TABLES: Record<ContainerKind, 'projects' | 'areas' | 'resources'> = {
  project: 'projects',
  area: 'areas',
  resource: 'resources',
};

/** The column on tasks and notes that points at each kind of container. */
export const CONTAINER_COLUMNS: Record<ContainerKind, 'project_id' | 'area_id' | 'resource_id'> = {
  project: 'project_id',
  area: 'area_id',
  resource: 'resource_id',
};

/** The page listing each kind; a container's own page is `${path}/${id}`. */
export const CONTAINER_PATHS: Record<ContainerKind, string> = {
  project: '/projects',
  area: '/areas',
  resource: '/resources',
};

export const containerPath = (kind: ContainerKind, id: string) => `${CONTAINER_PATHS[kind]}/${id}`;

/** Every container of the signed-in user, archived included, by name. */
export function useContainers() {
  return useQuery({
    queryKey: queryKeys.containers,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('containers')
        .select('kind, id, user_id, name, archived_at, updated_at')
        .order('name')
        .limit(LIST_LIMIT);
      if (error) throw error;
      return (data ?? []) as ContainerRow[];
    },
  });
}

/** Update one container. Throws when nothing was updated, so a silent no-op is never shown as saved. */
async function updateContainer(kind: ContainerKind, id: string, update: Record<string, unknown>): Promise<void> {
  const { data, error } = await supabase.from(CONTAINER_TABLES[kind]).update(update).eq('id', id).select('id');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error(`${CONTAINER_KIND_LABELS[kind].toLowerCase()} not found; it may have been deleted`);
}

export function renameContainer(kind: ContainerKind, id: string, name: string): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) return Promise.reject(new Error(`a ${CONTAINER_KIND_LABELS[kind].toLowerCase()} needs a name`));
  return updateContainer(kind, id, { name: trimmed });
}

/** Archive (hide it and everything in it from active views) or bring it back. */
export function setArchived(kind: ContainerKind, id: string, archived: boolean): Promise<void> {
  return updateContainer(kind, id, { archived_at: archived ? new Date().toISOString() : null });
}

export function setProjectStatus(id: string, status: ProjectStatus): Promise<void> {
  return updateContainer('project', id, { status });
}

/** Mark a project done and archive it. */
export function completeProject(id: string): Promise<void> {
  return updateContainer('project', id, { status: 'done', archived_at: new Date().toISOString() });
}

/** Create a container as the signed-in user and return its id. */
export async function createContainer(kind: ContainerKind, name: string): Promise<string> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error(`a ${CONTAINER_KIND_LABELS[kind].toLowerCase()} needs a name`);
  const userId = await signedInUserId();
  const { data, error } = await supabase
    .from(CONTAINER_TABLES[kind])
    .insert({ user_id: userId, name: trimmed })
    .select('id')
    .single();
  if (error) throw error;
  const id: unknown = data?.id;
  if (typeof id !== 'string') throw new Error('the server did not confirm the new container');
  return id;
}
